import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import Button from '@material-ui/core/Button'
import { makeStyles, Theme } from '@material-ui/core/styles'
import React from 'react'

/** Inactive-state palette for the Add New Project button, per the PBI 2831093 design spec. */
const DISABLED_BUTTON_TEXT_COLOR = '#4c5960'
const DISABLED_BUTTON_BACKGROUND_COLOR = '#d8dde0'
const DISABLED_BUTTON_BORDER_COLOR = '#36434a'

const useStyles = makeStyles((theme: Theme) => ({
    addProjectButton: {
        borderRadius: 100,
        color: theme.palette.common.white,
        fontWeight: 'normal',
        textTransform: 'capitalize',
        // Inactive-state values from the PBI design spec. They apply only when the
        // button is disabled — the enabled button keeps its original sizing, which
        // is why these live here rather than on the base rule.
        // `&&` doubles the class so the selector outranks MUI's own
        // `.MuiButton-contained.Mui-disabled` and `.MuiButtonBase-root.Mui-disabled`.
        '&&.Mui-disabled': {
            width: pxToRem(136),
            height: pxToRem(36),
            borderRadius: pxToRem(18),
            fontFamily: 'Arial, sans-serif',
            fontSize: pxToRem(14),
            fontWeight: 600,
            lineHeight: pxToRem(20),
            // The label needs ~115px in Arial 600 14px, but MUI's contained button
            // reserves 16px of padding each side, leaving only ~104px inside the
            // spec's fixed 136px width — enough to wrap "Add New Project" onto two
            // lines. Padding isn't part of the spec, so drop it to fit on one line.
            padding: 0,
            whiteSpace: 'nowrap',
            color: DISABLED_BUTTON_TEXT_COLOR,
            backgroundColor: DISABLED_BUTTON_BACKGROUND_COLOR,
            border: `1px solid ${DISABLED_BUTTON_BORDER_COLOR}`,
            boxShadow: 'none',
            // Spec: keep the component at full opacity rather than fading it.
            opacity: 1,
            // MUI's ButtonBase sets `pointer-events: none` on disabled buttons, which
            // suppresses the cursor the spec asks for. Restoring pointer events is safe:
            // a disabled <button> never dispatches click events.
            pointerEvents: 'auto',
            cursor: 'not-allowed',
        },
    },
    titleBarCont: {
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    addProject: {
        marginRight: theme.spacing(1),
        margin: 'auto',
    },
    projectTitleCont: {
        marginLeft: 0,
        margin: 'auto',
        padding: '6px 0px',
        fontSize: pxToRem(16),
    },
}))

interface ProjectTitleBarProps {
    projectTitle: string
    onAddClick: VoidFunction
    addProjectButton: boolean
    canCreateProject?: boolean
}
const ProjectTitleBar: React.FC<ProjectTitleBarProps> = ({
    projectTitle,
    onAddClick,
    addProjectButton,
    canCreateProject = true,
}) => {
    const classes = useStyles()
    return (
        <GridContainer direction="row" className={classes.titleBarCont}>
            <GridItem className={classes.projectTitleCont}>
                {projectTitle}
            </GridItem>
            {addProjectButton && (
                <GridItem className={classes.addProject}>
                    <Button
                        variant="contained"
                        color="primary"
                        className={classes.addProjectButton}
                        onClick={onAddClick}
                        disabled={!canCreateProject}
                        data-testid="btnAddNewProject"
                    >
                        Add New Project
                    </Button>
                </GridItem>
            )}
        </GridContainer>
    )
}

export default ProjectTitleBar
